/**
 * @file The screen wake lock held while a web upload runs (AKL-889), asked for so that
 * Safari grants it (AKL-1088).
 *
 * WebKit grants `navigator.wakeLock.request("screen")` only while the window has transient
 * activation — up to 5 s after a click or key press — or once an earlier request in the
 * same document has been granted that way (read from `WakeLock.cpp` and
 * `LocalDOMWindow.cpp`, 2026-09-30). Choosing files in the open panel gives the page no
 * activation, and a web upload starts from the folder input's `change` event, when the
 * panel closes; so a request made once the upload is in progress is usually refused. The
 * click that opens the picker is where the request has to be made. Chrome and Edge grant
 * it without a gesture.
 */

/** Events after which a refused wake lock is asked for again, inside the user's gesture. */
const RETRY_EVENTS = ["pointerup", "keydown"] as const;

/** See `createScreenWakeLock`. */
export interface ScreenWakeLock {
  /**
   * Asks for the lock now, synchronously. Call it from a click or key handler that starts
   * or continues an upload, before any `await`. With no upload in progress the lock is
   * kept for the upload about to start; call `forgoGestureRequest` if none does.
   */
  requestInGesture: () => void;
  /**
   * The gesture's upload did not start (picker dismissed, nothing chosen, folder refused):
   * lets go of the lock `requestInGesture` asked for. Does nothing while an upload is in
   * progress.
   */
  forgoGestureRequest: () => void;
  /**
   * An upload is in progress: holds the lock, asking for it unless it is held or already
   * asked for. Safe to call again — it is also the re-request when the tab is visible.
   */
  hold: () => void;
  /** The upload ended, or its provider is going away: lets go and stops asking. */
  release: () => void;
}

/**
 * Creates the screen wake lock controller for one upload provider.
 *
 * One request at a time, and none while a lock is held, so no sentinel is ever orphaned
 * (AKL-889 review cycle 5). A lock is kept while an upload is in progress, or while a
 * gesture has asked for it ahead of an upload that has not started yet. The browser
 * releases it when the page is hidden; it is asked for again when the browser lets it go
 * while the page is still visible, and by `hold` when the page is visible again. A refused
 * request made while an upload is in progress is retried on the user's next `pointerup` or
 * `keydown` on the page, which runs inside that gesture — `pointerup` rather than
 * `pointerdown` because a touch activates the page only on release, and for a mouse it
 * follows the `pointerdown` that did. Those listeners stay until a lock is held or the
 * upload ends. Nothing here is ever shown to the user: every refusal is swallowed.
 *
 * @returns the controller
 */
export const createScreenWakeLock = (): ScreenWakeLock => {
  let sentinel: WakeLockSentinel | null = null;
  let requesting = false;
  let uploading = false;
  let primed = false;
  let listening = false;

  const wanted = () => uploading || primed;

  const request = () => {
    if (!navigator.wakeLock || document.visibilityState !== "visible") return;
    if (sentinel || requesting || !wanted()) return;
    requesting = true;
    let pending: Promise<WakeLockSentinel>;
    try {
      pending = navigator.wakeLock.request("screen");
    } catch {
      pending = Promise.reject(); // a promise API, but never leave `requesting` stuck
    }
    pending.then(
      (granted) => {
        requesting = false;
        if (!wanted()) {
          // The upload ended, or the gesture's upload never started, while this request
          // was in flight: hold nothing rather than leave a sentinel nobody releases.
          granted.release().catch(() => {});
          return;
        }
        sentinel = granted;
        stopListening();
        granted.addEventListener(
          "release",
          () => {
            if (sentinel === granted) {
              sentinel = null;
            }
            if (uploading) {
              request();
            }
          },
          { once: true },
        );
      },
      () => {
        requesting = false;
        if (uploading) {
          listen();
        }
      },
    );
  };

  const onInteraction = () => request();

  const listen = () => {
    if (listening) return;
    listening = true;
    RETRY_EVENTS.forEach((type) =>
      window.addEventListener(type, onInteraction, true),
    );
  };

  const stopListening = () => {
    if (!listening) return;
    listening = false;
    RETRY_EVENTS.forEach((type) =>
      window.removeEventListener(type, onInteraction, true),
    );
  };

  const letGo = () => {
    const held = sentinel;
    sentinel = null;
    held?.release().catch(() => {
      // already released by the browser (e.g. the tab was hidden)
    });
  };

  return {
    requestInGesture: () => {
      if (!uploading) {
        primed = true;
      }
      request();
    },
    forgoGestureRequest: () => {
      if (uploading) return;
      primed = false;
      letGo();
    },
    hold: () => {
      uploading = true;
      primed = false;
      request();
    },
    release: () => {
      // Cleared before the release, so the sentinel's `release` event does not re-arm it.
      uploading = false;
      primed = false;
      stopListening();
      letGo();
    },
  };
};
